/**
 * Composite WorkWise fields for step 3, styled only with the classes of
 * NewDeploymentPage.module.css (disk list, tag list, selects, hints).
 */
import { useEffect } from 'react'
import { CheckCircle2, Loader2, TriangleAlert, XCircle } from 'lucide-react'
import { Controller, type Path, useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import clsx from 'clsx'
import { DEFAULT_APP_DATA_DISK_GB, DISK_TYPES, MAX_DISKS, MAX_EXTRA_TAGS, MAX_NSG_RULES } from '@/config/workwise'
import type { AvailabilityState } from '@/hooks/useWorkwise'
import { useSharedServers } from '@/hooks/useWorkwise'
import type { WorkwiseFlow } from '@/hooks/useWorkwiseFlow'
import styles from '@/pages/NewDeploymentPage.module.css'
import type { SharedServerRole, WorkwiseFormValues } from '@/types/workwise'
import type { AutoFieldPath } from '@/utils/workwise/autoFields'
import { sharedServerHint, sharedServerLabel } from '@/utils/workwise/sharedServers'
import { defaultDiskType } from '@/utils/workwise/defaults'
import { errorAt, fieldId } from './fieldHelpers'
import { Field, TextField } from './fields'

type FormPath = Path<WorkwiseFormValues>

// ─── Customer number availability ────────────────────────────────────────────

export function AvailabilityHint({ state }: { state: AvailabilityState }) {
  if (state.status === 'idle') return null
  const icon = { verticalAlign: '-2px', marginRight: 4 }
  if (state.status === 'taken') {
    return (
      <span className={styles.errorMsg} role="status" data-testid="availability-status" data-state="taken">
        <XCircle size={12} style={icon} />
        {state.message}
      </span>
    )
  }
  const content = {
    checking: { node: <Loader2 size={12} className={styles.spin} style={icon} />, text: 'Checking availability…' },
    available: { node: <CheckCircle2 size={12} style={{ ...icon, color: 'var(--aptean-success)' }} />, text: 'Available in this environment' },
    error: { node: <TriangleAlert size={12} style={icon} />, text: 'Could not check availability; the server checks again on Deploy.' },
  }[state.status]
  return (
    <div className={styles.fieldHint} role="status" data-testid="availability-status" data-state={state.status}>
      {content.node}
      {content.text}
    </div>
  )
}

// ─── VM name with its suggestion ─────────────────────────────────────────────

interface VmNameFieldProps {
  flow: WorkwiseFlow
  path: Extract<AutoFieldPath, 'appServer.vmName' | 'dbServer.vmName' | 'gatewayServer.vmName'>
  label: string
  hint: string
}

/** Pre-filled from GET /workwise/vm-names/; editable but validated. "Use …" restores the suggestion. */
export function VmNameField({ flow, path, label, hint }: VmNameFieldProps) {
  const { control } = useFormContext<WorkwiseFormValues>()
  const value = useWatch({ control, name: path }) as string | undefined
  const suggestion = flow.auto.suggestions[path].value
  const differs = suggestion !== '' && (value ?? '') !== suggestion
  return (
    <TextField
      path={path}
      label={label}
      required
      hint={hint}
      action={
        differs ? (
          <button type="button" className={styles.addTagBtn} data-testid={`use-suggested-${path}`} onClick={() => flow.auto.applySuggestion(path)}>
            Use {suggestion}
          </button>
        ) : undefined
      }
    />
  )
}

// ─── Shared server select ────────────────────────────────────────────────────

interface SharedServerSelectProps {
  role: SharedServerRole
  path: 'dbServer.sharedServerId' | 'gatewayServer.sharedServerId'
  label: string
}

/** Registered shared servers for the chosen region + environment; unselectable ones are disabled. */
export function SharedServerSelect({ role, path, label }: SharedServerSelectProps) {
  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext<WorkwiseFormValues>()
  const [region, environment] = useWatch({ control, name: ['region', 'environment'] })
  const selectedId = useWatch({ control, name: path }) as number | undefined
  const query = useSharedServers(role, region, environment)
  const servers = query.data
  const error = errorAt(errors, path)
  const selected = servers?.find((s) => s.id === selectedId)

  // A pick from another region/environment, or one that became unavailable, must not survive.
  useEffect(() => {
    if (servers && selectedId != null && !servers.some((s) => s.id === selectedId && s.selectable)) {
      setValue(path, undefined as never, { shouldValidate: false })
    }
  }, [servers, selectedId, path, setValue])

  const noun = role === 'db' ? 'database' : 'gateway'
  return (
    <div className={styles.formGroup}>
      <label className={styles.label} htmlFor={fieldId(path)}>
        {label} <span className={styles.required}>*</span>
      </label>
      <Controller
        control={control}
        name={path as FormPath}
        render={({ field }) => (
          <select
            id={fieldId(path)}
            data-testid={`field-${path}`}
            className={clsx(styles.select, error && styles.inputError)}
            aria-invalid={error ? true : undefined}
            disabled={!servers || servers.length === 0}
            value={field.value == null ? '' : String(field.value)}
            onBlur={field.onBlur}
            onChange={(e) => field.onChange(e.target.value === '' ? undefined : Number(e.target.value))}
          >
            <option value="">{query.isPending ? 'Loading shared servers…' : `— Select a shared ${noun} server —`}</option>
            {(servers ?? []).map((s) => (
              <option key={s.id} value={s.id} disabled={!s.selectable}>
                {sharedServerLabel(s)}
              </option>
            ))}
          </select>
        )}
      />
      {selected ? (
        <div className={styles.fieldHint} data-testid={`shared-info-${role}`}>
          {sharedServerHint(selected)}
        </div>
      ) : query.isError ? (
        <div className={styles.fieldHint}>
          Could not load the shared {noun} servers.{' '}
          <button type="button" className={styles.addTagBtn} onClick={() => void query.refetch()}>
            Retry
          </button>
        </div>
      ) : servers && servers.length === 0 ? (
        <div className={styles.fieldHint}>No shared {noun} servers are registered for this region and environment. Choose the Dedicated model instead.</div>
      ) : (
        <div className={styles.fieldHint}>Filtered by the region and environment above; full or unhealthy servers cannot be picked.</div>
      )}
      {error && (
        <span className={styles.errorMsg} role="alert" data-testid={`error-${path}`}>
          {error}
        </span>
      )}
    </div>
  )
}

// ─── Data disks (the "+ Add Disk" look), the App server's and the gateway's ──

type DiskListPath = 'appServer.disks' | 'gatewayServer.disks'

const DISK_LIST: Record<DiskListPath, { server: string; testId: string }> = {
  'appServer.disks': { server: 'App server', testId: 'ww-add-disk' },
  'gatewayServer.disks': { server: 'Gateway', testId: 'ww-add-disk-gateway' },
}

/**
 * Size (GB) + type per disk, "+ Add Disk" and remove; the App server and dedicated gateway lists (R3-G).
 * A dedicated DB has the "SQL Server storage" panel instead (DbStorageFields).
 */
export function DataDisksField({ path = 'appServer.disks' }: { path?: DiskListPath }) {
  const {
    control,
    register,
    formState: { errors },
  } = useFormContext<WorkwiseFormValues>()
  const { fields, append, remove } = useFieldArray({ control, name: path })
  // A new disk is Standard SSD outside production (the RnD subscriptions deny Premium disks).
  const environment = useWatch({ control, name: 'environment' })
  // A list-level error ("at least one" / "at most 16") sits on the path, or on its `root` once rows exist.
  const listError = errorAt(errors, path) ?? errorAt(errors, `${path}.root`)
  const { server, testId } = DISK_LIST[path]
  return (
    <div className={styles.formGroup} style={{ marginTop: 16 }} data-testid={`disks-${path}`}>
      <label className={styles.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Data Disks</span>
        <button
          type="button"
          className={styles.addTagBtn}
          disabled={fields.length >= MAX_DISKS}
          data-testid={testId}
          onClick={() => append({ sizeGb: DEFAULT_APP_DATA_DISK_GB, type: defaultDiskType(environment) })}
        >
          + Add Disk
        </button>
      </label>
      {fields.length === 0 ? (
        <div className={styles.emptyHint}>No data disks configured</div>
      ) : (
        <div className={styles.diskList}>
          {fields.map((field, i) => {
            const sizePath = `${path}.${i}.sizeGb` as const
            const sizeError = errorAt(errors, sizePath)
            return (
              <div key={field.id}>
                <div className={styles.diskRow}>
                  <input
                    type="number"
                    min={4}
                    max={32767}
                    aria-label={`${server} data disk ${i + 1} size in GB`}
                    aria-invalid={sizeError ? true : undefined}
                    data-testid={`field-${sizePath}`}
                    className={clsx(styles.diskInput, sizeError && styles.inputError)}
                    {...register(sizePath, { valueAsNumber: true })}
                  />
                  <span className={styles.diskUnit}>GB</span>
                  <select
                    aria-label={`${server} data disk ${i + 1} type`}
                    data-testid={`field-${path}.${i}.type`}
                    className={styles.diskInput}
                    style={{ width: 170 }}
                    {...register(`${path}.${i}.type` as const)}
                  >
                    {DISK_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                  <button type="button" className={styles.removeBtn} aria-label={`Remove ${server.toLowerCase()} data disk ${i + 1}`} onClick={() => remove(i)}>
                    ×
                  </button>
                </div>
                {sizeError && <span className={styles.errorMsg}>{sizeError}</span>}
              </div>
            )
          })}
        </div>
      )}
      {listError && (
        <span className={styles.errorMsg} role="alert" data-testid={`error-${path}`}>
          {listError}
        </span>
      )}
    </div>
  )
}

// ─── Gateway NSG rules (the tag-list look) ───────────────────────────────────

export function NsgRulesField() {
  const {
    control,
    register,
    formState: { errors },
  } = useFormContext<WorkwiseFormValues>()
  const { fields, append, remove } = useFieldArray({ control, name: 'gatewayServer.nsgRules' })
  return (
    <div className={styles.formGroup} style={{ marginTop: 16 }}>
      <label className={styles.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Inbound Rules (NSG)</span>
        <button
          type="button"
          className={styles.addTagBtn}
          disabled={fields.length >= MAX_NSG_RULES}
          onClick={() => append({ name: '', port: 443, source: '' })}
        >
          + Add Rule
        </button>
      </label>
      {fields.length === 0 ? (
        <div className={styles.emptyHint}>No inbound rules</div>
      ) : (
        <div className={styles.tagList}>
          {fields.map((field, i) => {
            const rowErrors = ['name', 'port', 'source']
              .map((key) => errorAt(errors, `gatewayServer.nsgRules.${i}.${key}`))
              .filter(Boolean)
            return (
              <div key={field.id}>
                <div className={styles.tagRow}>
                  <input className={styles.tagKeyInput} placeholder="rule name" aria-label={`Rule ${i + 1} name`} {...register(`gatewayServer.nsgRules.${i}.name`)} />
                  <input
                    type="number"
                    min={1}
                    max={65535}
                    className={styles.diskInput}
                    placeholder="port"
                    aria-label={`Rule ${i + 1} port`}
                    {...register(`gatewayServer.nsgRules.${i}.port`, { valueAsNumber: true })}
                  />
                  <span className={styles.tagSep}>from</span>
                  <input className={styles.tagValInput} placeholder="source, e.g. 10.0.0.0/16" aria-label={`Rule ${i + 1} source`} {...register(`gatewayServer.nsgRules.${i}.source`)} />
                  <button type="button" className={styles.removeBtn} aria-label={`Remove rule ${i + 1}`} onClick={() => remove(i)}>
                    ×
                  </button>
                </div>
                {rowErrors.map((message) => (
                  <span key={message} className={styles.errorMsg} style={{ display: 'block' }}>
                    {message}
                  </span>
                ))}
              </div>
            )
          })}
        </div>
      )}
      <div className={styles.fieldHint}>Ports 22, 3389, 1433 and 5432 cannot be open to the internet (*, Internet or 0.0.0.0/0).</div>
    </div>
  )
}

// ─── Extra tags (the "Additional Tags" look) ─────────────────────────────────

export function ExtraTagsField() {
  const {
    control,
    register,
    formState: { errors },
  } = useFormContext<WorkwiseFormValues>()
  const { fields, append, remove } = useFieldArray({ control, name: 'tags.extra' })
  return (
    <Field path="tags.extra" label="Additional Tags" action={
      <button type="button" className={styles.addTagBtn} disabled={fields.length >= MAX_EXTRA_TAGS} onClick={() => append({ key: '', value: '' })}>
        + Add Tag
      </button>
    }>
      {fields.length === 0 ? (
        <div className={styles.emptyHint}>No tags — click Add Tag to label resources</div>
      ) : (
        <div className={styles.tagList}>
          {fields.map((field, i) => {
            const keyError = errorAt(errors, `tags.extra.${i}.key`)
            return (
              <div key={field.id}>
                <div className={styles.tagRow}>
                  <input className={clsx(styles.tagKeyInput, keyError && styles.inputError)} placeholder="key" aria-label={`Tag ${i + 1} key`} {...register(`tags.extra.${i}.key`)} />
                  <span className={styles.tagSep}>=</span>
                  <input className={styles.tagValInput} placeholder="value" aria-label={`Tag ${i + 1} value`} {...register(`tags.extra.${i}.value`)} />
                  <button type="button" className={styles.removeBtn} aria-label={`Remove tag ${i + 1}`} onClick={() => remove(i)}>
                    ×
                  </button>
                </div>
                {keyError && <span className={styles.errorMsg}>{keyError}</span>}
              </div>
            )
          })}
        </div>
      )}
    </Field>
  )
}
